import {
  Component,
  ViewEncapsulation,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { Menu } from '@grapecity/wijmo.input';
import { KYMW4601Component } from '../kymw4601/kymw4601.component';
import { KYMW4602Component } from '../kymw4602/kymw4602.component';
import { KYMW4600Model } from './kymw4600.model';
import { TagGroupInfo } from './vo/tag-group-info.model';
import { TagInfo } from './vo/tag-info.model';
import { KYMW00Model } from '../../../commons/kymw00/kymw00.model';
import { PlessDialogServiceSub } from '../../../commons/kymw00/pless-dialog-service-sub';
import { PlessViewUtilsSub } from '../../../commons/kymw00/pless-view-utils-sub.service';
import { PlessViewHelperSub } from '../../../commons/kymw00/pless-view-helper-sub';
import { GetTagListService } from './api-services/get-tag-list.service';
import { GetTagGroupListService } from './api-services/get-tag-group-list.service';
import { RegistTagGroupListService } from './api-services/regist-tag-group-list.service';
import { RegistTagListService } from './api-services/regist-tag-list.service';
import { FlexGrid, CellType } from '@grapecity/wijmo.grid';
import { EventArgs } from '@grapecity/wijmo';
import { DataGridUtil } from '../../../commons/kymw00/data-grid-util';
import { FlexGridFilter, FilterType } from '@grapecity/wijmo.grid.filter';
import { PlessAlert } from 'src/app/shared/pless-alert/pless-alert.component';

/**
 * タグ管理画面を管理するクラス
 *
 * @export KYMW4600Component
 * @class KYMW4600Component
 */
@Component({
  selector: 'app-kymw4600',
  templateUrl: 'kymw4600.component.html',
  styleUrls: ['kymw4600.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class KYMW4600Component
  extends PlessViewHelperSub
  implements OnInit, AfterViewInit
{
  public model: KYMW4600Model = new KYMW4600Model();

  private contextForTag: Menu;
  private contextForTagGroup: Menu;

  public newTagName = '';
  public newTagGroupName = '';

  private dummyId = 0;
  private selectedDummy = false;

  private static readonly MAX_NAME_LENGTH = 30;
  private static readonly MAX_SET_TAGS = 100;
  private static readonly MIN_AGGREGATION_TAGS = 2;
  private static readonly MAX_LENGTH_FOR_TAGS = 30;
  private static readonly DUMMY_PREFIX = 'dmy';

  private validationsForModify: {
    invalid: (m: KYMW4600Model) => boolean;
    code: string;
    messages: string[];
  }[] = [
    {
      invalid: m => m.tagListForEdit.some(_t => !_t.tagName),
      code: 'W0001',
      messages: ['タグ名'],
    },
    {
      invalid: m =>
        m.tagListForEdit.length !==
        new Set(m.tagListForEdit.map(_ => _.tagName)).size,
      code: 'W0029',
      messages: ['タグ名'],
    },
  ];

  private validationsForNewGroup: {
    invalid: (s: string) => boolean;
    code: string;
    messages: string[];
  }[] = [
    {
      invalid: s => !s,
      code: 'W0001',
      messages: ['タググループ名'],
    },
    {
      invalid: s =>
        this.model.tagGroupListForEdit.some(_ => s === _.tagGroupName),
      code: 'W0029',
      messages: ['タググループ名'],
    },
  ];

  private validationsForNewTag: {
    invalid: (s: string) => boolean;
    code: string;
    messages: string[];
  }[] = [
    {
      invalid: s => !s,
      code: 'W0001',
      messages: ['タグ名'],
    },
    {
      invalid: s => this.model.tagListForEdit.some(_ => s === _.tagName),
      code: 'W0029',
      messages: ['タグ名'],
    },
  ];

  private validationsForAdd: {
    invalid: (m: KYMW4600Model) => boolean;
    code: string;
    messages: string[];
  }[] = [
    {
      invalid: m => m.tagListForEdit.every(_t => !_t.check),
      code: 'E0103',
      messages: ['タグ'],
    },
    {
      invalid: m => m.tagGroupListForEdit.every(_t => !_t.check),
      code: 'E0103',
      messages: ['タググループ'],
    },
    {
      invalid: m => {
        return m.tagGroupListForEdit
          .filter(_g => _g.check)
          .some((_v: TagGroupInfo) => {
            const set = new Set([
              ..._v.tags.map(_t => _t.tagId),
              ...this.model.tagListForEdit
                .filter(_t => _t.check)
                .map(_t => _t.tagId),
            ]);
            return set.size > KYMW4600Component.MAX_SET_TAGS;
          });
      },
      code: 'W0033',
      messages: [
        'タググループ',
        'タグ数',
        KYMW4600Component.MAX_SET_TAGS.toString(),
      ],
    },
  ];

  @ViewChild('dtg_TagList', { static: false }) dtg_TagList: FlexGrid;
  @ViewChild('dtg_TagGroupList', { static: false }) dtg_TagGroupList: FlexGrid;
  @ViewChild('fltr_TagList', { static: false }) fltr_TagList: FlexGridFilter;
  @ViewChild('fltr_TagGroupList', { static: false })
  fltr_TagGroupList: FlexGridFilter;
  @ViewChild('txt_NewTagGroup', { static: false }) txt_TagGroup: ElementRef;

  /**
   * Creates an instance of KYMW4600Component.
   * @param {PlessDialogServiceSub} dialogService
   * @param {GetTagListService} getTagListService
   * @param {GetTagGroupListService} getTagGroupListService
   * @param {RegistTagGroupListService} registTagGroupListService
   * @param {RegistTagListService} registTagListService
   * @param {KYMW00Model} kymw00Model
   * @memberof KYMW4600Component
   */
  constructor(
    private dialogService: PlessDialogServiceSub,
    private getTagListService: GetTagListService,
    private getTagGroupListService: GetTagGroupListService,
    private registTagGroupListService: RegistTagGroupListService,
    private registTagListService: RegistTagListService,
    private kymw00Model: KYMW00Model,
    private elementRef: ElementRef
  ) {
    super(kymw00Model);
  }

  /**
   * コンポーネント初期化
   *
   * @memberof KYMW4600Component
   */
  public ngOnInit(): void {
    this.componentName = 'KYMW4600';

    // 初期表示用のAPIリクエスト
    this.getTagListService.execute(this.model);
    this.getTagGroupListService.execute(this.model);

    this.createContextMenu();
  }

  /**
   * ビュー初期化
   *
   * @memberof KYMW4600Component
   */
  public ngAfterViewInit(): void {
    if (this.txt_TagGroup) this.txt_TagGroup.nativeElement.focus();

    if (this.dtg_TagList) {
      // グリッドの初期状態で選択無効
      this.dtg_TagList.itemsSourceChanged.addHandler(
        (_g: FlexGrid, _e: EventArgs) => {
          _g.select(-1, -1);
        }
      );
      // チェックボックスのあたり判定
      this.dtg_TagList.hostElement.addEventListener('click', e => {
        if (this.isPermittedReadOnly) return;
        const ht = this.dtg_TagList.hitTest(e.pageX, e.pageY);
        if (ht.cellType === CellType.Cell && ht.col === 0) {
          const val = this.dtg_TagList.getCellData(ht.row, ht.col, false);
          this.dtg_TagList.setCellData(ht.row, ht.col, val ? false : true);
        }
      });
      // コンテキストメニュー表示 タグ
      this.dtg_TagList.hostElement.addEventListener('contextmenu', e => {
        e.preventDefault();
        const ht = this.dtg_TagList.hitTest(e.pageX, e.pageY);
        this.selectedDummy = false;
        const selTag = this.model.tagListForEdit.find((e, i) => ht.row === i);
        if (selTag && !/^\d{8}$/.test(selTag.tagId)) this.selectedDummy = true;
        DataGridUtil.showContextMenu(this.dtg_TagList, this.contextForTag, e);
      });
      // 列ごとのクラス指定
      this.dtg_TagList.itemFormatter = (panel, r, c, cell) => {
        if (CellType.Cell === panel.cellType && c === 0)
          cell.classList.add('check-cell');
      };
    }
    if (this.dtg_TagGroupList) {
      // グリッドの初期状態で選択無効
      this.dtg_TagGroupList.itemsSourceChanged.addHandler(
        (_g: FlexGrid, _e: EventArgs) => {
          _g.select(-1, -1);
          this.adjustCols();
        }
      );
      // チェックボックスのあたり判定
      this.dtg_TagGroupList.hostElement.addEventListener('click', e => {
        if (this.isPermittedReadOnly) return;
        const ht = this.dtg_TagGroupList.hitTest(e.pageX, e.pageY);
        if (ht.cellType === CellType.Cell && ht.col === 0) {
          const val = this.dtg_TagGroupList.getCellData(ht.row, ht.col, false);
          this.dtg_TagGroupList.setCellData(ht.row, ht.col, val ? false : true);
        }
      });
      // コンテキストメニュー表示 タググループ
      this.dtg_TagGroupList.hostElement.addEventListener('contextmenu', e => {
        e.preventDefault();
        DataGridUtil.showContextMenu(
          this.dtg_TagGroupList,
          this.contextForTagGroup,
          e
        );
      });
      // 列ごとのクラス指定
      this.dtg_TagGroupList.itemFormatter = (panel, r, c, cell) => {
        if (CellType.Cell === panel.cellType && c === 0)
          cell.classList.add('check-cell');
      };
    }

    // フィルタ設定
    if (this.fltr_TagList) {
      this.fltr_TagList.defaultFilterType = FilterType.Both;
      this.fltr_TagList.showSortButtons = false;
      this.fltr_TagList.filterColumns = ['tagName'];
    }
    if (this.fltr_TagGroupList) {
      this.fltr_TagGroupList.defaultFilterType = FilterType.Both;
      this.fltr_TagGroupList.showSortButtons = false;
      this.fltr_TagGroupList.filterColumns = ['tagGroupName','tagNames'];
    }
  }

  /**
   * ボタン押下 更新
   *
   * @memberof KYMW4600Component
   */
  public modify() {
    // バリデーション
    const invalidItem = this.validationsForModify.find(_ =>
      _.invalid(this.model)
    );
    if (invalidItem) {
      this.dialogService.openAlertGetMessage(
        invalidItem.code,
        invalidItem.messages
      );
      return;
    }
    // 変更箇所なし
    if (
      this.model.registTags.length < 1 &&
      this.model.deleteTags.length < 1 &&
      this.model.updateTags.length < 1 &&
      this.model.registTagGroups.length < 1 &&
      this.model.deleteTagGroups.length < 1 &&
      this.model.updateTagGroups.length < 1
    ) {
      this.dialogService.openAlertGetMessage('E0162');
      return;
    } else {
      this.dialogService.openAlertGetMessage(
        'Q0185',
        ['タグ、タググループ'],
        _c => {
          if (_c === PlessAlert.Yes) {
            this.registTagListService.execute(this.model, calbakForTag);
          }
        }
      );
    }

    const calbakForTag = () => {
      this.registTagGroupListService.execute(this.model, calbakForTagGroup);
    };
    const calbakForTagGroup = () => {
      this.dialogService.openAlertGetMessage(
        'I0106',
        ['タグ情報の更新が完了'],
        _e => {
          this.getTagListService.execute(this.model);
          this.getTagGroupListService.execute(this.model);
        }
      );
    };
  }

  /**
   * ボタン押下 タググループ追加
   *
   * @memberof KYMW4600Component
   */
  public addNewGroup() {
    // バリデーション
    const invalidItem = this.validationsForNewGroup.find(_ =>
      _.invalid(this.newTagGroupName)
    );
    if (invalidItem) {
      this.dialogService.openAlertGetMessage(
        invalidItem.code,
        invalidItem.messages
      );
      return;
    }

    // 新規行追加
    this.model.tagGroupListForEdit.push(
      new TagGroupInfo(this.getDummyId(), this.newTagGroupName, [])
    );
    this.newTagGroupName = '';
    this.dtg_TagGroupList.collectionView.refresh();
  }

  /**
   * ボタン押下 タグ追加
   *
   * @memberof KYMW4600Component
   */
  public addNewTag() {
    // バリデーション
    const invalidItem = this.validationsForNewTag.find(_ =>
      _.invalid(this.newTagName)
    );
    if (invalidItem) {
      this.dialogService.openAlertGetMessage(
        invalidItem.code,
        invalidItem.messages
      );
      return;
    }

    // 新規行追加
    this.model.tagListForEdit.push(
      new TagInfo(this.getDummyId(), this.newTagName)
    );
    this.newTagName = '';
    this.dtg_TagList.collectionView.refresh();
  }

  /**
   * ボタン押下 タグ集約
   *
   * @memberof KYMW4600Component
   */
  public aggregateTags() {
    // タグ集約画面を呼び出す処理
    const openModal = () => {
      const componentRef = this.dialogService.openModal(KYMW4601Component);
      const instance = componentRef.componentInstance as KYMW4601Component;
      instance.parent = this;
      const targetIds = this.model.tagListForEdit
        .filter(_t => _t.check)
        .filter(_t => /^\d{8}$/.test(_t.tagId))
        .map(_t => _t.tagId);
      instance.setPrams(
        this.model.tagListOrigin
          .filter(_t => targetIds.includes(_t.tagId))
          .map(_t => {
            return [_t.tagId, _t.tagName];
          })
      );
      instance.closeFunction = () => {
        componentRef.close();
        const params = instance.getParams();
        if (params && params.procDone) {
          this.getTagListService.execute(this.model);
          this.getTagGroupListService.execute(this.model);
        }
      };
    };

    // 確認ダイアログ
    if (
      this.model.registTags.length > 0 ||
      this.model.deleteTags.length > 0 ||
      this.model.updateTags.length > 0 ||
      this.model.registTagGroups.length > 0 ||
      this.model.deleteTagGroups.length > 0 ||
      this.model.updateTagGroups.length > 0
    ) {
      this.dialogService.openAlertGetMessage('Q0110', [], e => {
        if (e === PlessAlert.Yes) {
          const targetIds = this.model.tagListForEdit
            .filter(_t => _t.check)
            .filter(_t => /^\d{8}$/.test(_t.tagId))
            .map(_t => _t.tagId);
          this.getTagListService.execute(this.model, () => {
            this.model.tagListForEdit
              .filter(_t => targetIds.includes(_t.tagId))
              .forEach(_t => (_t.check = true));
          });
          const targetGroupIds = this.model.tagGroupListForEdit
            .filter(_t => _t.check)
            .filter(_t => /^\d{8}$/.test(_t.tagGroupId))
            .map(_t => _t.tagGroupId);
          this.getTagGroupListService.execute(this.model, () => {
            this.model.tagGroupListForEdit
              .filter(_t => targetGroupIds.includes(_t.tagGroupId))
              .forEach(_t => (_t.check = true));
          });
          // バリデーション 入力チェック
          if (
            this.model.tagListForEdit
              .filter(_t => _t.check)
              .filter(_t => /^\d{8}$/.test(_t.tagId)).length <
            KYMW4600Component.MIN_AGGREGATION_TAGS
          ) {
            this.dialogService.openAlertGetMessage('W0032', [
              '集約対象のタグ',
              KYMW4600Component.MIN_AGGREGATION_TAGS.toString(),
            ]);
            return;
          }
          openModal();
        }
      });
    } else {
      // バリデーション 入力チェック
      if (
        this.model.tagListForEdit
          .filter(_t => _t.check)
          .filter(_t => /^\d{8}$/.test(_t.tagId)).length <
        KYMW4600Component.MIN_AGGREGATION_TAGS
      ) {
        this.dialogService.openAlertGetMessage('W0032', [
          '集約対象のタグ',
          KYMW4600Component.MIN_AGGREGATION_TAGS.toString(),
        ]);
        return;
      }
      openModal();
    }
  }

  /**
   * ボタン押下 タググループにタグを追加
   *
   * @memberof KYMW4600Component
   */
  public addTags() {
    // バリデーション
    const invalidItem = this.validationsForAdd.find(_ => _.invalid(this.model));
    if (invalidItem) {
      this.dialogService.openAlertGetMessage(
        invalidItem.code,
        invalidItem.messages
      );
      return;
    }

    this.model.tagGroupListForEdit
      .filter(_g => _g.check)
      .forEach(_g => {
        this.model.tagListForEdit
          .filter(_t => _t.check)
          .forEach(_t => {
            if (!_g.tags.find(_ => _t.tagId === _.tagId)) {
              _g.tags.push({ tagId: _t.tagId, tagName: _t.tagName });
            }
          });
        _g.tags.sort((a, b) => a.tagId.localeCompare(b.tagId));
      });
    this.adjustCols();
    this.refreshGrid();
  }

  /**
   * ボタン押下 設定済みのタグ X
   *
   * @param groupId
   * @param removeId
   * @memberof KYMW4600Component
   */
  public removeTag(groupId: string, removeId: string) {
    if (this.isPermittedReadOnly) return;
    this.model.tagGroupListForEdit.forEach(_g => {
      if (groupId === _g.tagGroupId) {
        _g.tags = _g.tags.filter(_ => removeId !== _.tagId);
        this.dtg_TagGroupList.collectionView.refresh();
      }
    });
    this.adjustCols();
    this.dtg_TagGroupList.collectionView.refresh();
  }

  /**
   * コンテキストメニュー タグ削除
   *
   * @memberof KYMW4600Component
   */
  private delTag() {
    if (!(this.dtg_TagList.selectedItems[0] instanceof TagInfo)) return;
    const selItem = this.dtg_TagList.selectedItems[0];
    const removeId = selItem.tagId;
    this.model.tagListForEdit = this.model.tagListForEdit.filter(
      _t => selItem.tagId !== _t.tagId
    );
    this.model.tagGroupListForEdit.forEach(_g => {
      _g.tags = _g.tags.filter(_ => removeId !== _.tagId);
    });
    this.dtg_TagGroupList.collectionView.refresh();
    this.dtg_TagList.collectionView.refresh(); // タグ一覧も最新化
  }

  /**
   * コンテキストメニュー タググループ削除
   *
   * @memberof KYMW4600Component
   */
  private delGroup() {
    if (!(this.dtg_TagGroupList.selectedItems[0] instanceof TagGroupInfo))
      return;
    const selItem = this.dtg_TagGroupList.selectedItems[0];
    this.model.tagGroupListForEdit = this.model.tagGroupListForEdit.filter(
      _t => selItem.tagGroupId !== _t.tagGroupId
    );
    this.adjustCols();
    this.dtg_TagGroupList.collectionView.refresh();
  }

  /**
   * コンテキストメニュー Pコード一括紐づけ設定
   *
   * @memberof KYMW4600Component
   */
  private setPCode() {
    if (!(this.dtg_TagList.selectedItems[0] instanceof TagInfo)) return;
    const selTag = this.dtg_TagList.selectedItems[0];

    // タグ一括設定画面を呼び出す
    const componentRef = this.dialogService.openModal(KYMW4602Component);
    const instance = componentRef.componentInstance as KYMW4602Component;
    instance.parent = this;
    instance.setParams([selTag.tagId, selTag.tagName]);
    instance.closeFunction = () => {
      componentRef.close();
    };
  }

  /**
   * コンテキストメニュー作成
   *
   * @memberof KYMW4600Component
   */
  private createContextMenu(): void {
    this.contextForTag = new Menu(document.createElement('div'), {
      displayMemberPath: 'caption',
      selectedValuePath: 'cmd',
      itemsSource: [
        { caption: 'タグ削除', cmd: 'delTag', refuseDummy: false },
        {
          caption: 'Ｐコード一括紐づけ設定',
          cmd: 'setPCode',
          refuseDummy: true,
        },
      ],
      command: {
        canExecuteCommand: param => {
          return this.isPermittedFullControl && (!param.refuseDummy || !this.selectedDummy);
        },
      },
      itemClicked: () => {
        if (this.contextForTag.selectedValue === 'delTag') this.delTag();
        if (this.contextForTag.selectedValue === 'setPCode') this.setPCode();
      },
    });
    this.contextForTagGroup = new Menu(document.createElement('div'), {
      displayMemberPath: 'caption',
      selectedValuePath: 'cmd',
      itemsSource: [
        { caption: 'タググループ削除', cmd: 'delGroup', enabled: true },
      ],
      command: {
        canExecuteCommand: param => {
          return this.isPermittedFullControl && param.enabled;
        },
      },
      itemClicked: () => {
        if (this.contextForTagGroup.selectedValue === 'delGroup')
          this.delGroup();
      },
    });
  }

  /**
   * グリッドの選択状態初期化
   *
   * @memberof KYMW4600Component
   */
  private refreshGrid() {
    this.model.tagGroupListForEdit.forEach(_g => (_g.check = false));
    this.model.tagListForEdit.forEach(_t => (_t.check = false));
    this.dtg_TagGroupList.select(-1, -1);
    this.dtg_TagList.select(-1, -1);
    this.dtg_TagGroupList.collectionView.refresh();
    this.dtg_TagList.collectionView.refresh();
  }

  /**
   * 新規登録用ID
   *
   * @memberof KYMW4600Component
   */
  private getDummyId(): string {
    return (
      KYMW4600Component.DUMMY_PREFIX + String(this.dummyId++).padStart(5, '0')
    );
  }

  /**
   * 更新タグ名をタググループ反映
   *
   * @param id
   * @memberof KYMW4600Component
   */
  public updateTagName(id: string) {
    const name = this.model.tagListForEdit.find(_t => id === _t.tagId).tagName;
    this.model.tagGroupListForEdit.forEach(_g =>
      _g.tags.filter(_t => id === _t.tagId).forEach(_t => (_t.tagName = name))
    );
    this.adjustCols();
    this.dtg_TagGroupList.collectionView.refresh();
    this.dtg_TagList.collectionView.refresh(); // タグ一覧も最新化
  }

  /**
   * 設定済みのタグ カラム幅調節
   *
   * @memberof KYMW4600Component
   */
  private adjustCols() {
    if (!this.dtg_TagGroupList) return;

    // タググループ名の幅指定
    if (this.dtg_TagGroupList?.columns?.[1])
      this.dtg_TagGroupList.columns[1].width = 338;

    // 設定済みのタグの幅指定
    this.dtg_TagGroupList.rows.forEach(() => {
      // 自動補正
      this.dtg_TagGroupList.autoSizeColumns(2);
      // 短い場合は最小で536pxとする
      if(this.dtg_TagGroupList.columns[2].width < 536) {
        this.dtg_TagGroupList.columns[2].width = 536;
      }
    });
  }
}
